import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, CreditCard, AlertCircle } from 'lucide-react';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const RefundPolicyPage = () => {
  useEffect(() => { trackPageView('/refund-policy', 'Refund Policy'); }, []);

  const policies = [
    {
      icon: Clock,
      title: 'Cancellation Window',
      text: 'The company agrees to refund any order if canceled within 90 minutes of confirmation. This duration is kept to ensure in-time delivery to the user site.',
    },
    {
      icon: CreditCard,
      title: 'Refund Processing',
      text: 'Amount paid by the buyer would be credited to their credit card/bank account within 7 working days or before the date of cancellation. The company would not be held responsible for any delay in the bank\'s response to the act of returning the credit to the buyer\'s account.',
    },
    {
      icon: AlertCircle,
      title: 'Quantity Disputes',
      text: 'If for any reason, the quantity received is lower than ordered, a refund is to be given to the customer within 10 working days. The customer needs to send their complaint to sales@avicement.com.',
    },
  ];

  return (
    <div className="min-h-screen bg-void" data-testid="refund-policy-page">
      <SEOHead
        title="Refund Policy"
        description="Avi Cement Limited refund and cancellation policy for orders and services."
        path="/refund-policy"
        noindex
      />
      <section className="relative py-12 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="container mx-auto px-4 md:px-8 max-w-4xl relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-coral font-mono text-sm mb-4 block">LEGAL</span>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">Refund Policy</h1>
            <p className="text-gray-500 text-sm font-mono">Last updated: March 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="pb-20 md:pb-32">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="space-y-6">
            {policies.map((policy, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-surface border border-white/5 rounded-2xl p-8"
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-xl bg-coral/10 flex items-center justify-center flex-shrink-0">
                    <policy.icon className="w-6 h-6 text-coral" />
                  </div>
                  <div>
                    <h2 className="font-display text-lg font-semibold text-white mb-3">{policy.title}</h2>
                    <p className="text-gray-400 text-sm leading-relaxed">{policy.text}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 bg-surface border border-white/5 rounded-2xl p-8 text-center">
            <p className="text-gray-400 text-sm">For refund-related queries, contact us at</p>
            <a href="mailto:sales@avicement.com" className="text-coral font-display font-medium hover:underline">sales@avicement.com</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicyPage;
